import React, { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import MouseOutlinedIcon from "@mui/icons-material/MouseOutlined";
import OpenWithOutlinedIcon from "@mui/icons-material/OpenWithOutlined";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import FlipToFrontOutlinedIcon from "@mui/icons-material/FlipToFrontOutlined";
import FlipToBackOutlinedIcon from "@mui/icons-material/FlipToBackOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import CropOutlinedIcon from "@mui/icons-material/CropOutlined";
import FilterNoneOutlinedIcon from "@mui/icons-material/FilterNoneOutlined";
import GestureOutlinedIcon from "@mui/icons-material/GestureOutlined";
import HorizontalRuleOutlinedIcon from "@mui/icons-material/HorizontalRuleOutlined";
import CallMadeOutlinedIcon from "@mui/icons-material/CallMadeOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import CropSquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";
import AutoFixOffOutlinedIcon from "@mui/icons-material/AutoFixOffOutlined";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import SelectAllOutlinedIcon from "@mui/icons-material/SelectAllOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import SvgIcon from "@mui/material/SvgIcon";

function ShapeToCircleIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 16 }} {...props}>
      <rect
        x="3.25"
        y="3.25"
        width="17.5"
        height="17.5"
        rx="1.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="2.4 1.8"
      />
      <circle
        cx="12"
        cy="12"
        r="5.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.85"
      />
    </SvgIcon>
  );
}

function ShapeToSquareIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 16 }} {...props}>
      <rect
        x="3.25"
        y="3.25"
        width="17.5"
        height="17.5"
        rx="1.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.85"
      />
      <circle
        cx="12"
        cy="12"
        r="5.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="2.4 1.8"
      />
    </SvgIcon>
  );
}

function Kbd({ children }) {
  return (
    <Box
      component="kbd"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 28,
        px: 0.85,
        py: 0.35,
        borderRadius: 1.25,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
        boxShadow:
          "0 1px 0 rgba(15,23,42,0.14), inset 0 -1px 0 rgba(15,23,42,0.04)",
        fontSize: "0.72rem",
        fontWeight: 800,
        fontFamily:
          "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        letterSpacing: 0.02,
        lineHeight: 1,
        color: "text.primary",
      }}
    >
      {children}
    </Box>
  );
}

function KeyCombo({ parts }) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
    >
      {parts.map((part, i) => (
        <React.Fragment key={`${part}-${i}`}>
          {i > 0 ? (
            <Typography
              component="span"
              variant="caption"
              color="text.disabled"
              sx={{ fontWeight: 700, px: 0.1 }}
            >
              +
            </Typography>
          ) : null}
          <Kbd>{part}</Kbd>
        </React.Fragment>
      ))}
    </Stack>
  );
}

function Gesture({ icon, label }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: 1.25,
          bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
          color: "text.secondary",
          border: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      {label ? (
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {label}
        </Typography>
      ) : null}
    </Stack>
  );
}

const BOARD_TOOL_IDS = ["zoomOut", "zoomPct", "zoomIn"];
const BOARD_TOOL_ICONS = {
  zoomOut: ZoomOutOutlinedIcon,
  zoomIn: ZoomInOutlinedIcon,
};

const TABLET_TOOL_IDS = ["undo", "redo", "multi", "pan"];
const TABLET_TOOL_ICONS = {
  undo: UndoOutlinedIcon,
  redo: RedoOutlinedIcon,
  multi: SelectAllOutlinedIcon,
  pan: PanToolOutlinedIcon,
};

const PAGE_TOOL_IDS = ["tabs", "add", "del", "aviator", "auto"];
const PAGE_TOOL_ICONS = {
  tabs: FilterNoneOutlinedIcon,
  add: AddOutlinedIcon,
  del: DeleteOutlineIcon,
  aviator: ViewQuiltOutlinedIcon,
  auto: GridViewOutlinedIcon,
};

const SLOT_TOOL_DEFS = [
  { id: "addRect", icon: CropFreeOutlinedIcon, custom: false },
  { id: "addCircle", icon: CircleOutlinedIcon, custom: false },
  { id: "toCircle", custom: "circle" },
  { id: "toSquare", custom: "square" },
  { id: "front", icon: FlipToFrontOutlinedIcon, custom: false },
  { id: "back", icon: FlipToBackOutlinedIcon, custom: false },
  { id: "rembg", icon: AutoFixHighOutlinedIcon, custom: false },
  { id: "stroke", icon: BorderColorOutlinedIcon, custom: false },
  { id: "crop", icon: CropOutlinedIcon, custom: false },
];

const MARK_TOOL_IDS = [
  "select",
  "pen",
  "line",
  "arrow",
  "ellipse",
  "rect",
  "text",
  "eraser",
];
const MARK_TOOL_ICONS = {
  select: NearMeOutlinedIcon,
  pen: GestureOutlinedIcon,
  line: HorizontalRuleOutlinedIcon,
  arrow: CallMadeOutlinedIcon,
  ellipse: RadioButtonUncheckedOutlinedIcon,
  rect: CropSquareOutlinedIcon,
  text: TitleOutlinedIcon,
  eraser: AutoFixOffOutlinedIcon,
};

function SectionTitle({ children }) {
  return (
    <Typography fontWeight={800} variant="body2" gutterBottom>
      {children}
    </Typography>
  );
}

function Body({ children, paragraph = false }) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      paragraph={paragraph}
      component="div"
    >
      {children}
    </Typography>
  );
}

function ToolRows({ rows }) {
  return (
    <Stack spacing={1} sx={{ mt: 0.75 }}>
      {rows.map((row) => (
        <Stack
          key={row.id}
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 0.5, sm: 1.5 }}
          alignItems={{ sm: "center" }}
        >
          <Box
            sx={{
              flexShrink: 0,
              minWidth: { sm: 148 },
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Gesture icon={row.icon} />
            <Typography
              variant="caption"
              fontWeight={800}
              color="text.primary"
              sx={{ lineHeight: 1.2 }}
            >
              {row.label}
            </Typography>
          </Box>
          <Body>{row.text}</Body>
        </Stack>
      ))}
    </Stack>
  );
}

export default function SkillAgentTasksHelpDialog({ open, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const keyboard = useMemo(
    () => [
      {
        id: "multi",
        keys: (
          <KeyCombo
            parts={[
              t("skillAgent.help.kbd.shift"),
              t("skillAgent.help.kbd.click"),
            ]}
          />
        ),
        text: t("skillAgent.help.keyboard.multi"),
      },
      {
        id: "multiMode",
        keys: (
          <Gesture
            icon={<SelectAllOutlinedIcon sx={{ fontSize: 16 }} />}
            label={t("skillAgent.moodboard.multiSelect")}
          />
        ),
        text: t("skillAgent.help.keyboard.multiMode"),
      },
      {
        id: "marquee",
        keys: (
          <Gesture
            icon={<MouseOutlinedIcon sx={{ fontSize: 16 }} />}
            label={t("skillAgent.help.kbd.dragEmpty")}
          />
        ),
        text: t("skillAgent.help.keyboard.marquee"),
      },
      {
        id: "drag",
        keys: (
          <Gesture
            icon={<OpenWithOutlinedIcon sx={{ fontSize: 16 }} />}
            label={t("skillAgent.help.kbd.dragSlot")}
          />
        ),
        text: t("skillAgent.help.keyboard.drag"),
      },
      {
        id: "pan",
        keys: (
          <KeyCombo
            parts={[
              t("skillAgent.help.kbd.alt"),
              t("skillAgent.help.kbd.drag"),
            ]}
          />
        ),
        text: t("skillAgent.help.keyboard.pan"),
      },
      {
        id: "zoom",
        keys: (
          <KeyCombo
            parts={[
              t("skillAgent.help.kbd.alt"),
              t("skillAgent.help.kbd.wheel"),
            ]}
          />
        ),
        text: t("skillAgent.help.keyboard.zoom"),
      },
      {
        id: "handles",
        keys: (
          <Gesture
            icon={<CropFreeOutlinedIcon sx={{ fontSize: 16 }} />}
            label={t("skillAgent.help.kbd.handles")}
          />
        ),
        text: t("skillAgent.help.keyboard.handles"),
      },
      {
        id: "undo",
        keys: (
          <KeyCombo
            parts={[t("skillAgent.help.kbd.ctrl"), t("skillAgent.help.kbd.z")]}
          />
        ),
        text: t("skillAgent.help.keyboard.undo"),
      },
      {
        id: "redo",
        keys: (
          <KeyCombo
            parts={[t("skillAgent.help.kbd.ctrl"), t("skillAgent.help.kbd.y")]}
          />
        ),
        text: t("skillAgent.help.keyboard.redo"),
      },
      {
        id: "esc",
        keys: <KeyCombo parts={[t("skillAgent.help.kbd.esc")]} />,
        text: t("skillAgent.help.keyboard.esc"),
      },
      {
        id: "del",
        keys: (
          <KeyCombo
            parts={[
              t("skillAgent.help.kbd.del"),
              t("skillAgent.help.kbd.backspace"),
            ]}
          />
        ),
        text: t("skillAgent.help.keyboard.del"),
      },
    ],
    [t],
  );

  const boardTools = useMemo(
    () =>
      BOARD_TOOL_IDS.map((id) => {
        const Icon = BOARD_TOOL_ICONS[id];
        return {
          id,
          icon: Icon ? (
            <Icon sx={{ fontSize: 16 }} />
          ) : (
            <Typography
              component="span"
              sx={{ fontSize: "0.85rem", fontWeight: 800, lineHeight: 1 }}
            >
              {t(`skillAgent.help.boardLabels.${id}`)}
            </Typography>
          ),
          label: t(`skillAgent.help.boardLabels.${id}`),
          text: t(`skillAgent.help.boardTools.${id}`),
        };
      }),
    [t],
  );

  const tabletTools = useMemo(
    () =>
      TABLET_TOOL_IDS.map((id) => {
        const Icon = TABLET_TOOL_ICONS[id];
        return {
          id,
          icon: <Icon sx={{ fontSize: 16 }} />,
          label: t(`skillAgent.help.tabletLabels.${id}`),
          text: t(`skillAgent.help.tabletTools.${id}`),
        };
      }),
    [t],
  );

  const pagesTools = useMemo(
    () =>
      PAGE_TOOL_IDS.map((id) => {
        const Icon = PAGE_TOOL_ICONS[id];
        return {
          id,
          icon: <Icon sx={{ fontSize: 16 }} />,
          label: t(`skillAgent.help.pagesLabels.${id}`),
          text: t(`skillAgent.help.pagesTools.${id}`),
        };
      }),
    [t],
  );

  const slotsTools = useMemo(
    () =>
      SLOT_TOOL_DEFS.map(({ id, icon: Icon, custom }) => ({
        id,
        icon:
          custom === "circle" ? (
            <ShapeToCircleIcon />
          ) : custom === "square" ? (
            <ShapeToSquareIcon />
          ) : (
            <Icon sx={{ fontSize: 16 }} />
          ),
        label: t(`skillAgent.help.slotsLabels.${id}`),
        text: t(`skillAgent.help.slotsTools.${id}`),
      })),
    [t],
  );

  const marksTools = useMemo(
    () =>
      MARK_TOOL_IDS.map((id) => {
        const Icon = MARK_TOOL_ICONS[id];
        return {
          id,
          icon: <Icon sx={{ fontSize: 16 }} />,
          label: t(`skillAgent.help.marksLabels.${id}`),
          text: t(`skillAgent.help.marksTools.${id}`),
        };
      }),
    [t],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <HelpOutlineOutlinedIcon color="primary" />
          <Typography variant="h6" fontWeight={800}>
            {t("skillAgent.help.title")}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.75, fontWeight: 400 }}
        >
          <Trans i18nKey="skillAgent.help.intro" components={{ b: <b /> }} />
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.18),
            }}
          >
            <SectionTitle>{t("skillAgent.help.sectionKeyboard")}</SectionTitle>
            <Stack spacing={1.1} sx={{ mt: 0.75 }}>
              {keyboard.map((row) => (
                <Stack
                  key={row.id}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 0.5, sm: 1.5 }}
                  alignItems={{ sm: "center" }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      minWidth: { sm: 176 },
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {row.keys}
                  </Box>
                  <Body>{row.text}</Body>
                </Stack>
              ))}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1.25 }}
            >
              {t("skillAgent.help.keyboardFootnote")}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.text.primary, 0.03),
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <SectionTitle>{t("skillAgent.help.sectionBoard")}</SectionTitle>
            <ToolRows rows={boardTools} />
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.06),
              border: "1px solid",
              borderColor: alpha(theme.palette.info.main, 0.2),
            }}
          >
            <SectionTitle>{t("skillAgent.help.sectionTablet")}</SectionTitle>
            <ToolRows rows={tabletTools} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1.25 }}
            >
              {t("skillAgent.help.tabletFootnote")}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.text.primary, 0.03),
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <SectionTitle>{t("skillAgent.help.sectionPages")}</SectionTitle>
            <ToolRows rows={pagesTools} />
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.16),
            }}
          >
            <SectionTitle>{t("skillAgent.help.sectionSlots")}</SectionTitle>
            <ToolRows rows={slotsTools} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1.25 }}
            >
              {t("skillAgent.help.slotsFootnote")}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.06),
              border: "1px solid",
              borderColor: alpha(theme.palette.warning.main, 0.2),
            }}
          >
            <SectionTitle>{t("skillAgent.help.sectionMarks")}</SectionTitle>
            <ToolRows rows={marksTools} />
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ display: "block", mt: 1.25 }}
            >
              <Trans
                i18nKey="skillAgent.help.marksFootnote"
                components={{ b: <b /> }}
              />
            </Typography>
          </Box>

          <Divider />

          <Box>
            <SectionTitle>{t("skillAgent.help.sectionTree")}</SectionTitle>
            <Body paragraph>
              <Trans
                i18nKey="skillAgent.help.treeBody1"
                components={{ b: <b /> }}
              />
            </Body>
            <Body>
              <Trans
                i18nKey="skillAgent.help.treeBody2"
                components={{ code: <code /> }}
              />
            </Body>
          </Box>

          <Divider />

          <Box>
            <SectionTitle>
              {t("skillAgent.help.sectionMoodboards")}
            </SectionTitle>
            <Body paragraph>
              <Trans
                i18nKey="skillAgent.help.moodboardsBody1"
                components={{ b: <b /> }}
              />
            </Body>
            <Body paragraph>
              <Trans
                i18nKey="skillAgent.help.moodboardsBody2"
                components={{ b: <b /> }}
              />
            </Body>
            <Body paragraph>
              <Trans
                i18nKey="skillAgent.help.moodboardsBody3"
                components={{ b: <b /> }}
              />
            </Body>
            <Body>
              <Trans
                i18nKey="skillAgent.help.moodboardsBody4"
                components={{ b: <b /> }}
              />
            </Body>
          </Box>

          <Box>
            <SectionTitle>{t("skillAgent.help.sectionSearchs")}</SectionTitle>
            <Body paragraph>
              <Trans
                i18nKey="skillAgent.help.searchsBody1"
                components={{ b: <b /> }}
              />
            </Body>
            <Body>{t("skillAgent.help.searchsBody2")}</Body>
          </Box>

          <Box>
            <SectionTitle>{t("skillAgent.help.sectionDesigns")}</SectionTitle>
            <Body paragraph>
              <Trans
                i18nKey="skillAgent.help.designsBody1"
                components={{ b: <b /> }}
              />
            </Body>
            <Body paragraph>
              <Trans
                i18nKey="skillAgent.help.designsBody2"
                components={{ b: <b /> }}
              />
            </Body>
            <Body>{t("skillAgent.help.designsBody3")}</Body>
          </Box>

          <Divider />

          <Box>
            <SectionTitle>{t("skillAgent.help.sectionDrive")}</SectionTitle>
            <Body>{t("skillAgent.help.driveBody")}</Body>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {t("skillAgent.help.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
